import { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBold,
  faItalic,
  faHeading,
  faListUl,
  faListOl,
  faQuoteLeft,
  faCode,
  faFileCode,
  faLink,
} from '@fortawesome/free-solid-svg-icons';
import {
  $createParagraphNode,
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
} from 'lexical';
import { $setBlocksType } from '@lexical/selection';
import { $createHeadingNode, $createQuoteNode, $isHeadingNode, $isQuoteNode } from '@lexical/rich-text';
import { $createCodeNode, $isCodeNode } from '@lexical/code';
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { $createLinkNode, $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { MARKDOWN_NODES, $loadMarkdownInto, editorStateToMarkdown, isSafeUrl } from './lexicalMarkdown';
import './markdownEditor.css';

// `list` uses Lexical's own vocabulary so it can be compared with `getListType()`.
const TOOLS = [
  { key: 'bold', label: 'Bold', icon: faBold, type: 'format', format: 'bold' },
  { key: 'italic', label: 'Italic', icon: faItalic, type: 'format', format: 'italic' },
  { key: 'heading1', label: 'Heading 1', icon: faHeading, type: 'block', block: 'h1', badge: '1' },
  { key: 'heading2', label: 'Heading 2', icon: faHeading, type: 'block', block: 'h2', badge: '2' },
  { key: 'heading3', label: 'Heading 3', icon: faHeading, type: 'block', block: 'h3', badge: '3' },
  { key: 'bulletedList', label: 'Bulleted list', icon: faListUl, type: 'list', list: 'bullet' },
  { key: 'numberedList', label: 'Numbered list', icon: faListOl, type: 'list', list: 'number' },
  { key: 'quote', label: 'Quote', icon: faQuoteLeft, type: 'block', block: 'quote' },
  { key: 'inlineCode', label: 'Inline code', icon: faCode, type: 'format', format: 'code' },
  { key: 'codeBlock', label: 'Code block', icon: faFileCode, type: 'block', block: 'code' },
  { key: 'link', label: 'Link', icon: faLink, type: 'link' },
];

const LABELS = {
  toolbar: 'Formatting',
  view: 'Editor view',
  write: 'Write',
  markdown: 'Markdown',
  link: 'Link',
  linkUrl: 'Link URL',
  linkApply: 'Apply',
  linkCancel: 'Cancel',
  hint: 'Markdown supported: **bold**, *italic*, # headings, - lists, > quotes, `code` and [links](url).',
};

const BLOCK_CREATORS = {
  h1: () => $createHeadingNode('h1'),
  h2: () => $createHeadingNode('h2'),
  h3: () => $createHeadingNode('h3'),
  quote: () => $createQuoteNode(),
  code: () => $createCodeNode(),
};

const EMPTY_ACTIVE = {
  bold: false,
  italic: false,
  code: false,
  link: false,
  linkUrl: '',
  heading: null,
  quote: false,
  list: null,
  codeBlock: false,
};

// Walks up from the caret so the toolbar can show which block and inline formats are
// already applied at the cursor, which is what makes the buttons feel like a real editor.
function readSelectionState(selection) {
  if (!$isRangeSelection(selection)) return EMPTY_ACTIVE;

  const anchor = selection.anchor.getNode();
  const state = { ...EMPTY_ACTIVE };

  for (let node = anchor; node; node = node.getParent()) {
    if ($isHeadingNode(node)) state.heading = node.getTag();
    else if ($isQuoteNode(node)) state.quote = true;
    else if ($isCodeNode(node)) state.codeBlock = true;
    else if ($isListNode(node) && !state.list) state.list = node.getListType();
    else if ($isLinkNode(node) && !state.linkUrl) {
      state.link = true;
      state.linkUrl = node.getURL();
    }
  }

  state.bold = anchor.hasFormat('bold');
  state.italic = anchor.hasFormat('italic');
  state.code = anchor.hasFormat('code');

  return state;
}

function useActiveState(editor) {
  const [active, setActive] = useState(EMPTY_ACTIVE);

  useEffect(() => {
    const sync = () => {
      editor.getEditorState().read(() => setActive(readSelectionState($getSelection())));
    };
    sync();
    return editor.registerUpdateListener(sync);
  }, [editor]);

  return active;
}

// Applies one toolbar action. Kept as a standalone function taking the editor so the
// formatting behaviour can be exercised against a headless editor in tests, where a real
// DOM selection cannot be created.
function applyTool(editor, tool, active) {
  if (tool.type === 'format') {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, tool.format);
    return;
  }

  if (tool.type === 'list') {
    if (active.list === tool.list) {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(
        tool.list === 'number' ? INSERT_ORDERED_LIST_COMMAND : INSERT_UNORDERED_LIST_COMMAND,
        undefined,
      );
    }
    return;
  }

  const isActive =
    (tool.block === 'quote' && active.quote) ||
    (tool.block === 'code' && active.codeBlock) ||
    (active.heading === tool.block);

  editor.update(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;
    $setBlocksType(selection, () => (isActive ? $createParagraphNode() : BLOCK_CREATORS[tool.block]()));
  });
}

// A selection becomes a link; a bare caret gets the url inserted as its own link so the
// button is never a no-op. Unsafe schemes are refused outright.
function applyLink(editor, url) {
  if (!isSafeUrl(url)) return;

  let collapsed = true;
  editor.getEditorState().read(() => {
    const selection = $getSelection();
    collapsed = !$isRangeSelection(selection) || selection.isCollapsed();
  });

  if (collapsed) {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      const link = $createLinkNode(url);
      link.append($createTextNode(url));
      selection.insertNodes([link]);
    });
    return;
  }

  editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
}

function isToolActive(tool, active) {
  if (tool.type === 'format') return Boolean(active[tool.format]);
  if (tool.type === 'link') return active.link;
  if (tool.type === 'list') return active.list === tool.list;
  if (tool.block === 'quote') return active.quote;
  if (tool.block === 'code') return active.codeBlock;
  return active.heading === tool.block;
}

function useToolbar(editor) {
  const active = useActiveState(editor);

  const run = useCallback((tool) => applyTool(editor, tool, active), [editor, active]);
  const isActive = useCallback((tool) => isToolActive(tool, active), [active]);

  return { active, run, isActive };
}

// A native window.prompt is unusable on the phones this app is mostly read on, so the link
// target is collected in a small inline field instead. Fully controlled: the toolbar seeds
// the draft at the moment it opens, so no effect needs to synchronise it.
function LinkEditor({ url, onUrlChange, onSubmit, onCancel }) {
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Deliberately not a <form>. This sits inside the page's own post <form>, and a nested
  // form is invalid HTML that the browser resolves by binding the submit button to the
  // *outer* form, so applying a link would submit the post and throw away the draft. Enter
  // is handled on the input instead.
  return (
    <div className="markdownEditor__linkBar">
      <input
        ref={inputRef}
        type="url"
        className="markdownEditor__linkInput"
        placeholder={LABELS.linkUrl}
        aria-label={LABELS.linkUrl}
        value={url}
        onChange={(event) => onUrlChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          onSubmit();
        }}
      />
      <button type="button" className="markdownEditor__linkAction" onClick={onSubmit}>
        {LABELS.linkApply}
      </button>
      <button type="button" className="markdownEditor__linkAction" onClick={onCancel}>
        {LABELS.linkCancel}
      </button>
    </div>
  );
}

function MarkdownToolbar({ disabled }) {
  const [editor] = useLexicalComposerContext();
  const { active, run, isActive } = useToolbar(editor);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  const onLinkClick = () => {
    setLinkUrl(active.linkUrl || '');
    setLinkOpen((open) => !open);
  };

  return (
    <div className="markdownEditor__toolArea">
      <div className="markdownEditor__toolbar" role="toolbar" aria-label={LABELS.toolbar}>
        {TOOLS.map((tool) => {
          const isOn = isActive(tool);
          return (
            <button
              key={tool.key}
              type="button"
              className={`markdownEditor__toolButton ${isOn ? 'markdownEditor__toolButton--active' : ''}`.trim()}
              // Keeps the caret where it is so the command acts on the right text.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => (tool.type === 'link' ? onLinkClick() : run(tool))}
              disabled={disabled}
              aria-pressed={isOn}
              title={tool.label}
              aria-label={tool.label}
            >
              <FontAwesomeIcon icon={tool.icon} aria-hidden="true" />
              {tool.badge ? <span className="markdownEditor__badge">{tool.badge}</span> : null}
            </button>
          );
        })}
      </div>
      {linkOpen && !disabled ? (
        <LinkEditor
          url={linkUrl}
          onUrlChange={setLinkUrl}
          onSubmit={() => {
            const target = linkUrl.trim();
            setLinkOpen(false);
            if (target) applyLink(editor, target);
          }}
          onCancel={() => setLinkOpen(false)}
        />
      ) : null}
    </div>
  );
}

// Keeps the Lexical tree in step with the markdown prop. Importing is skipped when the
// incoming markdown is exactly what the editor last produced, so ordinary typing never
// round-trips back through the parser. `editorMarkdownRef` is deliberately NOT updated by
// the raw-markdown textarea, which is what makes returning to rich text re-import.
function MarkdownBridge({ markdown, editorMarkdownRef, onChange }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (markdown === editorMarkdownRef.current) return;
    editor.update(() => $loadMarkdownInto(editor, markdown), { discrete: true });
    editorMarkdownRef.current = markdown;
  }, [editor, markdown, editorMarkdownRef]);

  return (
    <OnChangePlugin
      ignoreSelectionChange
      onChange={() => {
        const next = editor.getEditorState().read(() => editorStateToMarkdown(editor));
        editorMarkdownRef.current = next;
        onChange(next);
      }}
    />
  );
}

// Editability is owned by Lexical, not by a DOM attribute: `initialConfig.editable`
// covers the first render and this covers later `disabled` changes.
function EditableStatePlugin({ editable }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.setEditable(editable);
  }, [editor, editable]);

  return null;
}

function MarkdownEditor({
  value,
  onChange,
  placeholder,
  rows = 6,
  disabled = false,
  className = '',
  ariaLabel,
}) {
  const [mode, setMode] = useState('rich');
  const editorMarkdownRef = useRef(null);

  const initialConfig = {
    namespace: 'MarkdownEditor',
    nodes: MARKDOWN_NODES,
    editable: !disabled,
    onError: (error) => {
      throw error;
    },
  };

  return (
    <div
      className={`markdownEditor markdownEditor--${mode} ${className}`.trim()}
      data-mode={mode}
      style={{ '--md-rows': rows }}
    >
      <LexicalComposer initialConfig={initialConfig}>
        <div className="markdownEditor__bar">
          <MarkdownToolbar disabled={disabled} />
          {disabled ? null : (
            <div className="markdownEditor__modeSwitch" role="group" aria-label={LABELS.view}>
              <button
                type="button"
                className={`markdownEditor__modeButton ${mode === 'rich' ? 'markdownEditor__modeButton--active' : ''}`.trim()}
                onClick={() => setMode('rich')}
                aria-pressed={mode === 'rich'}
              >
                {LABELS.write}
              </button>
              <button
                type="button"
                className={`markdownEditor__modeButton ${mode === 'markdown' ? 'markdownEditor__modeButton--active' : ''}`.trim()}
                onClick={() => setMode('markdown')}
                aria-pressed={mode === 'markdown'}
              >
                {LABELS.markdown}
              </button>
            </div>
          )}
        </div>

        <div className="markdownEditor__richPane" hidden={mode === 'markdown'}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="markdownEditor__editable"
                aria-label={ariaLabel}
                aria-readonly={disabled || undefined}
              />
            }
            placeholder={<div className="markdownEditor__placeholder">{placeholder}</div>}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin validateUrl={isSafeUrl} />
          <TabIndentationPlugin />
          <EditableStatePlugin editable={!disabled} />
          <MarkdownBridge markdown={value} editorMarkdownRef={editorMarkdownRef} onChange={onChange} />
        </div>
      </LexicalComposer>

      {mode === 'markdown' && !disabled ? (
        <div className="markdownEditor__markdownPane">
          <textarea
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder}
            rows={rows}
            aria-label={ariaLabel}
            className="markdownEditor__markdownTextarea"
          />
        </div>
      ) : null}

      <p className="markdownEditor__hint">{LABELS.hint}</p>
    </div>
  );
}

export { TOOLS, applyTool, applyLink, isToolActive, readSelectionState };
export default MarkdownEditor;
